import React, { useState } from "react";
import api, { apiError } from "@/api";
import { Check, Trash2, Lock, Unlock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PlanCard = ({ p, readOnly, onChanged }) => {
  const [notes, setNotes] = useState(p.closure_notes || "");
  const put = async (body, msg) => {
    try { await api.put(`/my-plans/${p.id}`, body); if (msg) toast.success(msg); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleAct = (i) => put({ actions: p.actions.map((a, j) => (j === i ? { ...a, done: !a.done } : a)) });
  const close = () => {
    if (p.progress < 100 && !window.confirm("لم تكتمل كل الإجراءات. إغلاق الخطة؟")) return;
    put({ status: "closed", closure_notes: notes }, "تم إغلاق الخطة العلاجية");
  };
  const remove = async () => {
    if (!window.confirm("حذف الخطة؟")) return;
    try { await api.delete(`/my-plans/${p.id}`); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const open = p.status === "open";
  return (
    <div data-testid={`plan-${p.id}`} className={`rounded-xl border-2 p-3 bg-white/85 ${open ? (p.overdue ? "border-red-400/60" : "border-amber-400/50") : "border-emerald-400/50"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-stone-800 truncate">{p.school_name}</div>
          <div className="text-[11px] text-stone-500 flex flex-wrap gap-2 mt-0.5">
            <span className={open ? "text-amber-700" : "text-emerald-700"}>{open ? "مفتوحة" : `مغلقة ${(p.closed_at || "").slice(0, 10)}`}</span>
            {p.due_date && <span>الموعد: {p.due_date}</span>}
            {p.overdue && <span className="text-red-700 flex items-center gap-0.5"><AlertTriangle size={11} /> تجاوزت الموعد</span>}
          </div>
        </div>
        <span className="text-xs font-bold text-sky-800 shrink-0" data-testid={`plan-progress-${p.id}`}>{p.progress}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-stone-200 mt-2 overflow-hidden"><div className="h-full bg-gradient-to-l from-emerald-500 to-sky-400" style={{ width: `${p.progress}%` }} /></div>
      <p className="text-sm text-stone-700 mt-2">{p.problem}</p>
      <div className="mt-2 space-y-1">
        {p.actions.map((a, i) => (
          <button key={i} type="button" disabled={readOnly || !open} data-testid={`plan-${p.id}-act-${i}`} onClick={() => toggleAct(i)}
            className="w-full flex items-center gap-2 text-right text-sm disabled:cursor-default">
            <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${a.done ? "bg-emerald-600 text-white" : "border border-stone-300"}`}>{a.done && <Check size={12} />}</span>
            <span className={a.done ? "line-through text-stone-400" : "text-stone-700"}>{a.text}</span>
          </button>
        ))}
      </div>
      {!open && p.closure_notes && <p className="text-xs text-emerald-800 mt-2">ملاحظات الإغلاق: {p.closure_notes}</p>}
      {!readOnly && (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          {open ? (
            <>
              <input data-testid={`plan-close-notes-${p.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="نتيجة المعالجة (اختياري)" className="flex-1 min-w-[140px] px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-400/30 text-xs" />
              <button data-testid={`plan-close-${p.id}`} onClick={close} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs flex items-center gap-1"><Lock size={12} /> إغلاق الخطة</button>
            </>
          ) : (
            <button data-testid={`plan-reopen-${p.id}`} onClick={() => put({ status: "open" }, "أُعيد فتح الخطة")} className="px-3 py-1.5 rounded-lg border border-amber-400/60 text-amber-700 text-xs flex items-center gap-1"><Unlock size={12} /> إعادة فتح</button>
          )}
          <button data-testid={`plan-del-${p.id}`} onClick={remove} className="p-1.5 rounded-lg hover:bg-red-500/15 text-red-700"><Trash2 size={14} /></button>
        </div>
      )}
    </div>
  );
};

export const RemedialPlans = ({ plans, readOnly, onChanged }) => {
  if (!plans.length) return <div className="text-center text-stone-500 text-sm py-8" data-testid="plans-empty">لا توجد خطط علاجية. أنشئ خطة لكل مدرسة متأخرة وتابعها حتى تُغلق.</div>;
  return <div className="grid md:grid-cols-2 gap-3" data-testid="plans-list">{plans.map((p) => <PlanCard key={p.id} p={p} readOnly={readOnly} onChanged={onChanged} />)}</div>;
};

export default RemedialPlans;
